'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import InlineGuessReveal from '@/app/components/blog/InlineGuessReveal'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastic Testing: What a Lab Can Actually Measure — and Why &ldquo;None Detected&rdquo; Isn&rsquo;t Clean"
      author="MicroPlastics Research Desk"
      date="August 13, 2026"
      readTime="12 min read"
      heroImage="/blog/heroes/microplastics-water-filters-compared.webp"
      heroImageAlt="Microplastic testing in 2026: what laboratory methods can measure, what mail-in kits report, and why a clean result reflects the detection limit rather than the water"
    >
      <BlogLastUpdated iso="2026-08-13" formatted="August 13, 2026" />

      <BlogTLDR>
        <strong>You can pay a laboratory to test your water, and the result
        will be real. It will also have a floor underneath it that nobody puts
        on the report.</strong>{' '}
        Three instruments do genuine microplastic identification:
        micro-FTIR, micro-Raman and pyrolysis-GC/MS. They cost{' '}
        <strong>$30,000 to $300,000</strong>, need a trained operator, and each
        one stops seeing particles at a certain size.{' '}
        <strong>FTIR gives up around 10 micrometres. Raman gets to roughly
        0.5&ndash;1.</strong> That matters more than any other number here,
        because the particles turning up in human blood, placenta and brain
        tissue are largely <em>smaller</em> than the ones a water test can
        resolve. So a report reading &ldquo;none detected&rdquo; does not mean
        your water is clean. It means nothing was found{' '}
        <strong>above the method&rsquo;s detection limit</strong> — a sentence
        that is doing an enormous amount of quiet work. There is still{' '}
        <strong>no clinical test</strong> that will tell you what is in your
        body, at any price.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <><strong>Three methods count as real testing:</strong> micro-FTIR, micro-Raman, and pyrolysis-GC/MS. Everything else is particle counting, which is not the same thing.</>,
          <><strong>Every method has a size floor.</strong> FTIR stops at roughly <strong>10 &micro;m</strong>, Raman at about <strong>0.5&ndash;1 &micro;m</strong>. Nanoplastics are below both — they are not &ldquo;absent&rdquo;, they are invisible to the instrument.</>,
          <><strong>Py-GC/MS tells you mass, not count.</strong> It burns the sample, so it reports how much polymer by weight and which type — and destroys any information about particle size, shape or number.</>,
          <><strong>&ldquo;None detected&rdquo; is a statement about the instrument</strong>, not about the water. Always read it as &ldquo;none above the limit of detection&rdquo;.</>,
          <><strong>Mail-in consumer kits are legitimate but limited.</strong> The good ones use optical and polarised-light microscopy with Raman confirmation. Filter-and-look kits cannot tell plastic from sand, mineral or cotton.</>,
          <><strong>ISO 16094-2 only arrived in September 2025.</strong> Before that there was no international standard for microplastic analysis of water, which is why older numbers from different labs are not comparable.</>,
          <><strong>You cannot get yourself tested.</strong> No clinical or commercial test exists for microplastics in human blood or tissue. The studies you have read used donated samples and research instruments.</>,
        ]}
      />

      <KeyStats
        heading="The numbers that decide what a test can see"
        stats={[
          { value: '~10 µm', label: 'size floor for micro-FTIR', context: 'the workhorse method for water; below this it stops identifying particles', source: 'ITRC, Microplastics — Sampling and Analysis', sourceUrl: 'https://mp-1.itrcweb.org/sampling-and-analysis/' },
          { value: '0.5–1 µm', label: 'size floor for micro-Raman', context: 'the best routine resolution available, and still above the nanoplastic range', source: 'ITRC, Microplastics — Sampling and Analysis', sourceUrl: 'https://mp-1.itrcweb.org/sampling-and-analysis/' },
          { value: '$30k–$300k', label: 'cost of the instruments', context: 'plus a trained operator and a contamination-controlled room — the reason no home version exists', source: 'MicroPlastics, How to Test for Microplastics at Home (2026)', sourceUrl: 'https://microplasticsapp.com/blog/how-to-test-for-microplastics-at-home' },
          { value: 'Sept 2025', label: 'when ISO 16094-2 was published', context: 'the first international standard for microplastic analysis of clean water; Parts 3 and 4 are still pending', source: 'ISO 16094-2 / Measurlabs', sourceUrl: 'https://measurlabs.com/blog/iso-16094-standards-for-microplastic-analysis-of-water-samples/' },
          { value: 'Zero', label: 'clinical tests for microplastics in your body', context: 'no accredited lab will test your blood, stool or tissue for microplastics as a consumer service', source: 'MicroPlastics Research Desk, August 2026' },
        ]}
      />

      <h2>What &ldquo;testing&rdquo; actually means</h2>
      <p>
        There is a gap between two questions that sound identical.{' '}
        <em>Are there particles in this water?</em> is easy, and you can almost
        answer it with a coffee filter and a cheap microscope.{' '}
        <em>Are those particles plastic, and which plastic?</em> is the hard
        one, and it is the only one that counts.
      </p>
      <p>
        A fibre under a microscope can be polyester, cotton, wool or rayon, and
        at 20 micrometres they look broadly alike. A fragment can be polyethylene
        or it can be a grain of silica.{' '}
        <strong>Identification requires reading the material&rsquo;s chemistry,
        not its shape</strong>, and that is what separates a laboratory result
        from a photograph of some specks on a filter.
      </p>
      <p>
        Three instruments do that job. They work in genuinely different ways,
        and — this is the part that gets lost — they answer genuinely different
        questions.
      </p>

      <BlogComparisonTable
        caption="The three laboratory methods used for microplastic identification, and what each one can and cannot tell you (checked August 2026)"
        columns={[
          { key: 'method', label: 'Method' },
          { key: 'how', label: 'How it identifies plastic' },
          { key: 'floor', label: 'Smallest particle' },
          { key: 'gives', label: 'What you get' },
          { key: 'cost', label: 'Trade-off' },
        ]}
        rows={[
          { method: 'Micro-FTIR (µFTIR)', how: 'Infrared absorption fingerprint of the polymer', floor: '~10 µm', gives: 'Count, size, shape and polymer type', cost: 'Blind below 10 µm; handles cloudy water well' },
          { method: 'Micro-Raman (µRaman)', how: 'Scattered-light fingerprint of molecular bonds', floor: '~0.5–1 µm', gives: 'Count, size, shape and polymer type', cost: 'Best resolution, but pigments and organic matter cause fluorescence that swamps the signal' },
          { method: 'Pyrolysis-GC/MS', how: 'Burns the sample and identifies the decomposition products', floor: 'Not size-based — measures mass', gives: 'Polymer mass and polymer type', cost: 'Destroys the particles: no count, no size, no shape' },
          { method: 'Optical microscopy alone', how: 'It does not — it only shows that something is there', floor: 'Whatever the lens resolves', gives: 'A particle count of unknown composition', cost: 'Cannot distinguish plastic from sand, mineral or cotton' },
        ]}
      />

      <p>
        Read the last column and the awkwardness becomes obvious.{' '}
        <strong>The method with the best size resolution is the one most easily
        defeated by ordinary dirt, and the method that gives you a clean mass
        number destroys the particles to get it.</strong> There is no single
        instrument that answers every question at once, which is why serious
        studies often run two and why two labs can hand you different-looking
        numbers for the same bottle without either being wrong.
      </p>

      <InlineGuessReveal
        source="microplastic_testing_detection_floor"
        question="A standard lab water test comes back &ldquo;none detected&rdquo;. What has actually been ruled out?"
        options={[
          { label: 'All microplastics in the sample' },
          { label: 'Only particles above the method’s size limit', correct: true },
          { label: 'All plastics except PET' },
          { label: 'Nothing — the test is meaningless' },
        ]}
        answer="Only particles above the size limit — roughly 10 µm for FTIR"
        answerNote={
          <>
            Every analytical method has a limit of detection, and below it the
            instrument cannot distinguish a real signal from noise. For µFTIR
            that boundary sits near 10 micrometres. Particles smaller than that
            are not reported as absent because they are absent; they are not
            reported because the method never had access to them. It is the
            single most misread line on a laboratory report.
          </>
        }
        ctaText="The packaging your water arrives in is easier to check than the water."
        ctaLabel="Scan a bottle"
      />

      <RiskQuizInlineNudge source="blog_inline_microplastic_testing" />

      <h2>What you can actually buy in 2026</h2>
      <p>
        Mail-in water testing is a real, functioning consumer market, and the
        reputable end of it is not a scam. You collect a sample in supplied
        bottles under fairly strict instructions, post it to an accredited
        laboratory, and receive a report some weeks later. The better services
        combine optical and polarised-light microscopy for counting with{' '}
        <strong>Raman confirmation for polymer identification</strong>, which is
        the step that makes it a microplastic test rather than a particle count.
      </p>
      <p>
        What you should expect for your money, and what you should not:
      </p>

      <BlogComparisonTable
        caption="What a mail-in microplastic water test does and does not deliver"
        columns={[
          { key: 'q', label: 'The question' },
          { key: 'a', label: 'Can a consumer lab test answer it?' },
        ]}
        rows={[
          { q: 'How many particles are in my tap water?', a: 'Yes — a count, with size distribution' },
          { q: 'Which polymers are they?', a: 'Yes, for the larger particles, via Raman' },
          { q: 'Is my filter working?', a: 'Yes, if you test before and after — the single best use of a test' },
          { q: 'Are there nanoplastics in my water?', a: 'No. Below the detection floor for every consumer method' },
          { q: 'Is this level safe to drink?', a: 'No. There is no health-based threshold to compare against' },
          { q: 'How does my result compare to my neighbour’s?', a: 'Only if both used the same method — see ISO 16094 below' },
          { q: 'What is in my body?', a: 'No. No such consumer test exists' },
        ]}
      />

      <p>
        The third row is the one worth paying for.{' '}
        <strong>A single test of your water tells you a number you cannot
        interpret. Two tests — one before your filter and one after — tell you
        something you can act on immediately.</strong> Whatever the absolute
        counts are, the ratio between them is a direct measurement of whether
        the device on your counter is doing its job. If you are going to spend
        money on testing once, spend it that way. Our{' '}
        <Link href="/blog/microplastics-water-filters-compared" className="text-forest underline">comparison of water filters</Link>{' '}
        covers which technologies should be expected to show a difference.
      </p>

      <h2>The floor problem, which is the real story</h2>
      <p>
        Here is the tension nobody selling a test is keen to explain.
      </p>
      <p>
        The research that made people care about microplastics in the first
        place is research on <em>small</em> particles. The studies finding
        plastic in placenta, in blood, in arterial plaque and in brain tissue
        are overwhelmingly working at the bottom of the size range and below it,
        in the sub-micrometre and nanoplastic region. That is precisely because
        small particles are the ones that cross membranes. A 50-micrometre
        fragment does not enter a cell. A 200-nanometre one is a different
        physical proposition entirely.
      </p>
      <p>
        <strong>Now put that next to the detection floors. The consumer test you
        can buy sees down to about 1 to 10 micrometres. The particles that
        drove the health concern are, in large part, below that line.</strong>
      </p>
      <p>
        This does not make testing worthless — knowing your filter works is
        genuinely useful, and the larger fraction is real. But it does mean a
        reassuring report should be read carefully.{' '}
        <em>&ldquo;None detected&rdquo; is a statement about an instrument, not
        a clean bill of health for your water.</em> Any laboratory worth using
        will state its limit of detection somewhere on the report. That number
        is the most important thing on the page, and it is almost always in the
        smallest type.
      </p>
      <p>
        For what is known about the particles that do get into people, see{' '}
        <Link href="/blog/microplastics-in-the-brain" className="text-forest underline">microplastics in the brain</Link>{' '}
        and{' '}
        <Link href="/blog/are-microplastics-dangerous-health-evidence" className="text-forest underline">what the health evidence actually shows</Link>.
      </p>

      <BlogAppCTA
        headline="Cheaper than a lab test, and available now"
        sub="You cannot test your body, and testing your water has a floor. What you can do is see which products around you are the plastic sources in the first place — scan a product with MicroPlastics for a 0–100 score."
      />

      <h2>Why older results do not compare</h2>
      <p>
        Until very recently there was no international standard for this at all.
        Every laboratory made its own defensible choices about filter pore size,
        the smallest particle it would count, how it removed organic matter, and
        how it controlled for the polyester fibres shedding off the analyst&rsquo;s
        own clothing. Those choices move the final number by more than the
        differences between water sources do.
      </p>
      <p>
        <strong>ISO 16094-2 was published in September 2025</strong>, covering
        microplastic analysis of clean water, with Parts 3 and 4 still to come.
        It is the beginning of comparability rather than the arrival of it. The
        practical consequence for anyone reading numbers today:
      </p>
      <ul>
        <li>
          <strong>Two results from different labs are not directly
          comparable</strong> unless both state the same method and the same
          size cut-off. A lab counting from 1 &micro;m will always report more
          particles than one counting from 10 &micro;m, testing the identical
          bottle.
        </li>
        <li>
          <strong>Headline figures from before 2025 carry method variation
          inside them.</strong> This is a large part of why published bottled
          water counts range across orders of magnitude.
        </li>
        <li>
          <strong>Ask which standard was used.</strong> A laboratory that
          answers &ldquo;ISO 16094-2&rdquo; is telling you something meaningful.
          One that cannot name a method is telling you something too.
        </li>
      </ul>

      <h2>The test that does not exist</h2>
      <p>
        This is the most common question we get and the answer is unambiguous.{' '}
        <strong>You cannot pay anyone to tell you how many microplastics are in
        your body.</strong> There is no accredited clinical assay, no
        direct-to-consumer blood panel, and no validated stool test for
        microplastics. Where you see one offered, treat it as a red flag rather
        than a breakthrough.
      </p>
      <p>
        The reason is not squeamishness — it is contamination control. Detecting
        a handful of plastic particles in a few millilitres of blood means
        proving they did not come from the sample tube, the needle, the lab air
        or the analyst&rsquo;s sleeve. Research groups manage it with blank
        controls, filtered air and glass everything, and even then the field
        argues about the results. That level of control cannot survive a
        postal-service workflow, which is exactly why the studies you have read
        about are studies and not services.
      </p>

      <h2>What to actually do</h2>
      <ul>
        <li>
          <strong>Test the filter, not the water.</strong> A before-and-after
          pair on the same day, same tap, same lab is worth more than any single
          absolute number you will ever receive.
        </li>
        <li>
          <strong>Read the limit of detection first.</strong> Before the
          headline count, find the size cut-off. It reframes everything above
          it, and a report without one should not be trusted.
        </li>
        <li>
          <strong>Insist on polymer identification.</strong> If the method is
          microscopy alone with no FTIR or Raman step, you have bought a
          particle count and not a microplastic test.
        </li>
        <li>
          <strong>Ignore any at-home kit promising instant results.</strong> The
          identification step requires instruments that cost more than a car.
          Nothing arriving in an envelope with a colour chart is doing it. Our{' '}
          <Link href="/blog/how-to-test-for-microplastics-at-home" className="text-forest underline">honest guide to at-home testing</Link>{' '}
          goes through what the DIY options can and cannot show.
        </li>
        <li>
          <strong>Do not buy a body test.</strong> It does not exist. If someone
          is selling one, that fact tells you what you need to know about them.
        </li>
        <li>
          <strong>Consider skipping the test entirely.</strong> For most
          households the money is better spent on a filter with published
          particulate performance than on measuring a problem whose remedy you
          already know. Testing is for verifying a fix, not for deciding whether
          to bother.
        </li>
      </ul>

      <p>
        For where the exposure actually comes from, see{' '}
        <Link href="/blog/microplastics-in-tap-water" className="text-forest underline">microplastics in tap water</Link>{' '}
        and{' '}
        <Link href="/blog/microplastics-in-bottled-water" className="text-forest underline">microplastics in bottled water</Link>.
        For the filter question specifically, see{' '}
        <Link href="/blog/do-brita-filters-remove-microplastics" className="text-forest underline">do Brita filters remove microplastics</Link>.
      </p>

      <BlogFAQ
        items={[
          {
            question: 'Can you test for microplastics?',
            answer: 'Yes, for water, sediment and some foods, through an accredited laboratory. Genuine identification uses micro-FTIR, micro-Raman or pyrolysis-GC/MS — instruments costing $30,000 to $300,000 that require a trained operator and contamination-controlled conditions. Consumer mail-in services exist and the reputable ones use microscopy for counting with Raman confirmation for polymer identification. What does not exist at any price is a clinical test for microplastics in your own body.',
          },
          {
            question: 'How much can a microplastic test actually detect?',
            answer: 'Every method has a size floor. Micro-FTIR generally stops identifying particles below about 10 micrometres, and micro-Raman reaches roughly 0.5 to 1 micrometre. Neither can see into the nanoplastic range. This matters because much of the research linking microplastics to human tissue concerns particles at or below those limits, so a water test can return a low result while saying nothing about the smallest fraction.',
          },
          {
            question: 'Does "none detected" mean my water has no microplastics?',
            answer: 'No. It means no particles were found above the method’s limit of detection. If the laboratory counted from 10 micrometres upward, anything smaller was never in scope. Read the limit of detection stated on the report — usually in the method notes — because it defines what the result is actually claiming. A result with no stated detection limit should not be relied on.',
          },
          {
            question: 'Are at-home microplastic test kits accurate?',
            answer: 'Filter-based kits that ask you to look at a membrane can show that particles are present, but not whether they are plastic rather than sand, mineral, cotton or biological debris — and that distinction is the entire test. Mail-in kits are different: you collect the sample and an accredited laboratory does the analysis with real instruments. Those are legitimate. The distinction is not the packaging, it is whether a spectroscopic identification step happens somewhere.',
          },
          {
            question: 'Can I get my blood tested for microplastics?',
            answer: 'No. There is no accredited clinical assay or direct-to-consumer test for microplastics in blood, stool or tissue. The published studies use research-grade methods with rigorous contamination controls — filtered air, glass equipment, procedural blanks — because the main analytical difficulty is proving the particles did not come from the sampling process itself. That control cannot be maintained through a consumer postal workflow. Any service currently offering a personal microplastic body test should be treated with scepticism.',
          },
          {
            question: 'Why do different labs give different microplastic numbers?',
            answer: 'Because until recently there was no shared standard. Filter pore size, the smallest particle counted, the digestion method used to remove organic matter and the contamination controls all vary between laboratories, and each choice shifts the final count — often by more than genuine differences between water sources. ISO 16094-2, published in September 2025, is the first international standard for microplastic analysis of clean water, with further parts pending. Results obtained before it, or by labs not following it, are not directly comparable.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'Interstate Technology and Regulatory Council (ITRC)', year: 2026, title: 'Microplastics — Sampling and Analysis (micro-FTIR, micro-Raman and pyrolysis-GC/MS; lower size limits of approximately 10 µm for FTIR and 0.5 µm for Raman; thermal methods give polymer mass but destroy particle size, shape and count)', publisher: 'ITRC', url: 'https://mp-1.itrcweb.org/sampling-and-analysis/' },
          { authors: 'International Organization for Standardization', year: 2025, title: 'ISO 16094-2 — Water quality: analysis of microplastic in water, published September 2025; Parts 3 and 4 pending publication', publisher: 'ISO / Measurlabs summary', url: 'https://measurlabs.com/blog/iso-16094-standards-for-microplastic-analysis-of-water-samples/' },
          { authors: 'Measurlabs', year: 2026, title: 'How to detect microplastics in water samples — comparison of µFTIR and µRaman for turbid matrices and fluorescence interference', publisher: 'Measurlabs', url: 'https://measurlabs.com/blog/microplastics-in-water/' },
          { authors: 'MicroPlastics Research Desk', year: 2026, title: 'How to test for microplastics at home: what actually works — instrument costs of $30,000 to $300,000 and the limits of filter-based DIY kits', publisher: 'MicroPlastics', url: 'https://microplasticsapp.com/blog/how-to-test-for-microplastics-at-home' },
        ]}
      />
    </BlogPostLayout>
  )
}
